/**
 * features/shop/api.ts — queries para Tienda (lista + detalle + categorias).
 *
 * Hooks tipados que envuelven `useQuery` (TanStack Query) sobre el singleton
 * `supabase`. Cada `queryKey` es estable por filtros para que F6 (admin) pueda
 * invalidar bloques especificos al editar contenido.
 *
 * Reglas de los SELECTs:
 *  - Solo columnas que la UI consume (ahorra bytes y respeta RLS).
 *  - Filtros `is_active = true` + `deleted_at is null` en publicas.
 *  - Orden por `created_at desc` (lista) y `sort asc` (categorias).
 *  - Filtrado por categoria via embed inverso (`product_categories!inner(slug)`)
 *    cuando se pasa `categorySlug` — evita un round-trip extra para resolver id.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos derivados del schema. Solo las columnas que la UI lee.
// ---------------------------------------------------------------------------

type ProductRow = Database['public']['Tables']['products']['Row'];
type ProductCategoryRow = Database['public']['Tables']['product_categories']['Row'];
type CustomOrderInsert = Database['public']['Tables']['custom_orders']['Insert'];
type CustomOrderRow = Database['public']['Tables']['custom_orders']['Row'];

/** Product tal como lo consumen las pages: campos esenciales tipados. */
export interface Product {
  id: ProductRow['id'];
  title: ProductRow['title'];
  slug: ProductRow['slug'];
  summary: ProductRow['summary'];
  description_md: ProductRow['description_md'];
  price_cop: ProductRow['price_cop'];
  size: ProductRow['size'];
  material: ProductRow['material'];
  care_md: ProductRow['care_md'];
  edition: ProductRow['edition'];
  stock: ProductRow['stock'];
  is_handmade: ProductRow['is_handmade'];
  focus_tags: ProductRow['focus_tags'];
  category_id: ProductRow['category_id'];
  cover_url: ProductRow['cover_url'];
  gallery_urls: ProductRow['gallery_urls'];
  ratings_avg: ProductRow['ratings_avg'];
  ratings_count: ProductRow['ratings_count'];
}

/** Categoria de producto con icono simbolico opcional. */
export type ProductCategory = ProductCategoryRow;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Columnas seleccionadas para `Product`. Compartido por lista + detalle.
const PRODUCT_SELECT =
  'id, title, slug, summary, description_md, price_cop, size, material, care_md, edition, stock, is_handmade, focus_tags, category_id, cover_url, gallery_urls, ratings_avg, ratings_count';

/** Mapea fila cruda de Supabase al shape `Product` ya saneado. */
function mapProduct(row: Record<string, unknown>): Product {
  return {
    id: row.id as string,
    title: row.title as string,
    slug: row.slug as string,
    summary: (row.summary as string | null) ?? null,
    description_md: (row.description_md as string | null) ?? null,
    price_cop: row.price_cop as number,
    size: (row.size as string | null) ?? null,
    material: (row.material as string | null) ?? null,
    care_md: (row.care_md as string | null) ?? null,
    edition: row.edition as ProductRow['edition'],
    stock: (row.stock as number | null) ?? null,
    is_handmade: Boolean(row.is_handmade),
    focus_tags: (row.focus_tags as string[] | null) ?? [],
    category_id: (row.category_id as string | null) ?? null,
    cover_url: (row.cover_url as string | null) ?? null,
    gallery_urls: (row.gallery_urls as string[] | null) ?? [],
    ratings_avg: (row.ratings_avg as number | null) ?? 0,
    ratings_count: (row.ratings_count as number | null) ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Lista todos los productos activos. Si `categorySlug` esta definido, hace un
 * join inner contra `product_categories` filtrando por su slug — un solo
 * round-trip a la API. Orden: created_at desc.
 */
export function useProducts(categorySlug?: string): UseQueryResult<Product[], Error> {
  return useQuery<Product[], Error>({
    // queryKey segmentada por slug para invalidaciones quirurgicas.
    queryKey: ['products', 'list', categorySlug ?? '__all__'],
    queryFn: async (): Promise<Product[]> => {
      // Si filtramos por categoria, embed inverso !inner asegura que solo
      // vuelvan productos cuyo `category_id` matchee la categoria con el slug
      // pedido. Pedimos el id de la categoria como columna extra solo para
      // forzar el inner join (no se expone fuera del map).
      const selectExpr = categorySlug
        ? `${PRODUCT_SELECT}, product_categories!inner(slug)`
        : PRODUCT_SELECT;

      let q = supabase
        .from('products')
        .select(selectExpr)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (categorySlug) {
        // Filtra por la columna anidada usando la sintaxis de PostgREST.
        q = q.eq('product_categories.slug', categorySlug);
      }

      const { data, error } = (await q) as {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => mapProduct(row));
    },
  });
}

/** Catalogo de categorias ordenado por `sort asc`. */
export function useProductCategories(): UseQueryResult<ProductCategory[], Error> {
  return useQuery<ProductCategory[], Error>({
    queryKey: ['product_categories'],
    queryFn: async (): Promise<ProductCategory[]> => {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .is('deleted_at', null)
        .order('sort', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as ProductCategory[];
    },
  });
}

/**
 * Trae un unico producto activo por slug. `enabled` solo cuando `slug` existe
 * (evita disparar la query con string vacio en el primer render del router).
 *
 * `data === null` => producto no encontrado (componente debe mostrar EmptyState).
 */
export function useProductBySlug(slug: string | undefined): UseQueryResult<Product | null, Error> {
  return useQuery<Product | null, Error>({
    queryKey: ['products', 'detail', slug ?? ''],
    enabled: Boolean(slug),
    queryFn: async (): Promise<Product | null> => {
      const { data, error } = await supabase
        .from('products')
        .select(PRODUCT_SELECT)
        .eq('slug', slug as string)
        .eq('is_active', true)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return mapProduct(data as Record<string, unknown>);
    },
  });
}

// ---------------------------------------------------------------------------
// Encargo personalizado (custom_orders)
// ---------------------------------------------------------------------------

/**
 * Placeholder de referencia en `custom_orders.references` (jsonb).
 * El upload real a Storage vía Edge Function se conecta más adelante. Por
 * ahora solo preservamos metadata del archivo (filename/size/type) para
 * que el admin pueda revisar la solicitud y pedir las imágenes por correo
 * si las necesita.
 */
export interface CustomOrderReferencePlaceholder {
  filename: string;
  size: number;
  type: string;
}

/** Input que la UI pasa a `useSubmitCustomOrder`. */
export interface CustomOrderInput {
  /** Nullable: solo si `useAuth().user` esta presente. */
  customer_id?: string | null;
  name: string;
  email: string;
  phone?: string | null;
  city?: string | null;
  /** Tipo de pieza solicitada (slug del producto preseleccionado o categoria). */
  type?: string | null;
  intention?: string | null;
  /** Metadata de archivos cargados localmente. El upload a Storage se
   *  conecta más adelante. */
  references: CustomOrderReferencePlaceholder[];
  color_palette: string[];
  style?: string | null;
  size_text?: string | null;
  budget_range?: string | null;
  notes?: string | null;
  /**
   * Precio en COP enteros. Sólo se pasa desde el flujo de **compra directa**
   * (`TiendaEncargo` con `mode='direct'`) donde el producto ya tiene precio
   * fijo en `products.price_cop`. La edge function `create-payment-intent`
   * lee de `custom_orders.quoted_price_cop` para calcular `amountInCents`;
   * si no lo sembramos aquí, Wompi recibe monto 0 y rechaza el checkout.
   * En el flujo `custom` (encargo personalizado) queda `null` — el admin
   * cotiza más tarde antes de habilitar el pago.
   */
  quoted_price_cop?: number | null;
}

/** Fila retornada por la mutation (id + timestamps + status). */
export interface CustomOrderResult {
  id: CustomOrderRow['id'];
  status: CustomOrderRow['status'];
  created_at: CustomOrderRow['created_at'];
}

/**
 * Mutation: inserta una fila en `custom_orders`. Status fijo `received`.
 *
 * RLS: el insert anonimo lo admite la policy `custom_orders_insert_any`
 * (mig 0099). Si `customer_id` esta presente, queda vinculado al usuario;
 * de lo contrario el encargo queda como guest (`customer_id = null`).
 *
 * NOTA: el campo `references` es palabra reservada en SQL pero Supabase-JS
 * la maneja como columna normal en el objeto JS (envia jsonb). No requiere
 * escapes adicionales.
 */
export function useSubmitCustomOrder(): UseMutationResult<
  CustomOrderResult,
  Error,
  CustomOrderInput
> {
  const queryClient = useQueryClient();
  return useMutation<CustomOrderResult, Error, CustomOrderInput>({
    mutationFn: async (input: CustomOrderInput): Promise<CustomOrderResult> => {
      // Build payload exactamente con las columnas del schema. Status fijo a
      // 'received' segun §4.2 PROMPT_MAESTRO. quoted_price_cop / payment_id
      // los gestiona el admin mas tarde.
      const payload: CustomOrderInsert = {
        customer_id: input.customer_id ?? null,
        name: input.name,
        email: input.email,
        phone: input.phone ?? null,
        city: input.city ?? null,
        type: input.type ?? null,
        intention: input.intention ?? null,
        // jsonb -> array de placeholders. Más adelante se sustituirá por
        // refs reales a Storage.
        references:
          input.references as unknown as Database['public']['Tables']['custom_orders']['Insert']['references'],
        color_palette: input.color_palette,
        style: input.style ?? null,
        size_text: input.size_text ?? null,
        budget_range: input.budget_range ?? null,
        notes: input.notes ?? null,
        // Sembrado desde el cliente sólo en modo 'direct' con el precio del
        // producto ya fijado. En modo 'custom' queda null y admin lo cotiza.
        quoted_price_cop: input.quoted_price_cop ?? null,
        status: 'received',
      };

      // Pedimos solo las columnas que la UI consume (id + status + created_at).
      const { data, error } = await supabase
        .from('custom_orders')
        .insert(payload)
        .select('id, status, created_at')
        .single();
      if (error) throw new Error(error.message);
      return data as CustomOrderResult;
    },
    onSuccess: () => {
      // Invalida la bandeja admin (F6) si esta montada.
      void queryClient.invalidateQueries({ queryKey: ['custom_orders'] });
    },
  });
}
